import { Combobox, useCombobox } from '@mantine/core';
import clsx from 'clsx';
import { FC, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Language, LANGUAGES } from 'shared/lib';
import EnIcon from '../assets/en-icon.svg?react';
import EsIcon from '../assets/es-icon.svg?react';
import FrIcon from '../assets/fr-icon.svg?react';
import cls from './language-select.module.scss';

export type LanguageSelectProps = {
	className?: string;
	classNameDropdown?: string;
};

export const LANGUAGE_ICONS = {
	[LANGUAGES.en]: EnIcon,
	[LANGUAGES.fr]: FrIcon,
	[LANGUAGES.es]: EsIcon,
} satisfies Record<Language, FC<SVGProps<SVGSVGElement>>>;

export const LanguageSelect = ({ className, classNameDropdown }: LanguageSelectProps) => {
	const { i18n } = useTranslation();

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	const options = Object.entries(LANGUAGE_ICONS).map(([lng, Icon]) => (
		<Combobox.Option
			key={lng}
			data-test-id={`language-option-${lng}`}
			value={lng}
			className={cls.languageSelectOption}
		>
			<Icon />
			<span className={cls.languageSelectLabel}>{lng}</span>
		</Combobox.Option>
	));

	const Icon = LANGUAGE_ICONS[i18n.language as Language];

	return (
		<Combobox
			store={combobox}
			offset={1}
			onOptionSubmit={(lang) => {
				combobox.closeDropdown();
				i18n.changeLanguage(lang);
			}}
		>
			<Combobox.Target>
				<div
					data-test-id='language-select'
					className={clsx(cls.languageSelect, className)}
					onClick={() => combobox.toggleDropdown()}
				>
					<Icon />
					<span className={cls.languageSelectLabel}>{i18n.language}</span>
				</div>
			</Combobox.Target>
			<Combobox.Dropdown className={clsx(cls.languageSelectDropdown, classNameDropdown)}>
				<Combobox.Options className={cls.languageSelectDropdownOptions}>{options}</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
